import PropTypes from 'prop-types';
/* A tooltip component that supports buttons for dialog-like behavior */

import React from 'react';
import {debounce, throttle} from 'lodash';
import Button from 'common/components/button';
import {removePx, testViewportSize} from 'common/utils/dom-util';
import {t} from 'common/utils/translation-util';
import * as TooltipType from 'common/enum/tooltip-type';
import './index.scss';

// Values from: BOS-10384
const PAGE_MIN_MARGIN = 16;
const ARROW_MIN_MARGIN = 10 + PAGE_MIN_MARGIN; // Compound s, as measured from page

// Used in initial state, and when element is in center of page
const NO_MARGINS = Object.freeze({
  leftMargin: null,
  rightMargin: null,
  topMargin: null,
  bottomMargin: null,
});

// TODO:
// There is some edge case behavior that still needs to be fixed. This has to do
// with the fact that when it adjusts its location, it may seem like it doesn't
// need to adjust it again (since off screen), and thus causes "flickering" (e.g.
// on repeated resizes)
// For typical use of this component, it will not be encountered.

export default class Tooltip extends React.Component {
  static propTypes = {
    above: PropTypes.bool,
    absolute: PropTypes.bool,
    adjust: PropTypes.bool,
    adjustOnResize: PropTypes.bool,
    adjustOnScroll: PropTypes.bool,
    adjustVertical: PropTypes.bool,
    cancelLabel: PropTypes.string,
    children: PropTypes.any,
    confirmLabel: PropTypes.string,
    debounce: PropTypes.number,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    onDismiss: PropTypes.func,
    position: PropTypes.shape({
      top: PropTypes.number,
      left: PropTypes.number,
    }),
    tooltipType: PropTypes.string,
    visible: PropTypes.bool,
  };

  static defaultProps = {
    above: true,
    absolute: false,
    adjust: true,
    adjustOnResize: true,
    adjustOnScroll: false,
    adjustVertical: false,
    cancelLabel: t('Cancel'),
    children: null,
    confirmLabel: t('Confirm'),
    debounce: 100,
    onCancel: null,
    onConfirm: null,
    onDismiss: null,
    position: null,
    tooltipType: TooltipType.wide,
    visible: true,
  };

  static atEdge(state, ...marginNames) {
    for (const name of marginNames) {
      const value = state[`${name}Margin`];
      if (value !== null && value < PAGE_MIN_MARGIN) {
        return true;
      }
    }
    return false;
  }

  constructor(props) {
    super(props);
    this.debouncedAdjustLocation = debounce(
      () => this.adjustLocation(),
      this.props.debounce,
    );
    this.throttledHandleDismiss = throttle(
      (event) => this.handleDismiss(event),
      this.props.debounce,
    );
  }

  // Default state ignores margins
  state = NO_MARGINS;

  componentDidMount() {
    if (this.props.absolute) {
      this.handleAbsolute();
      return;
    }

    // Dismiss event, when clicking outside of tooltip
    if (this.props.onDismiss) {
      document.body.addEventListener('click', this.throttledHandleDismiss);
    }

    // Only visible + auto-adjusting components get events
    if (!this.props.visible || !this.props.adjust) {
      return;
    }

    // Note: these events do not cover all cases when we'd need to adjust its
    // Location (e.g. reflow of other elements due to interaction), however the
    // Solution for this is too expensive as it requires polling. If thats
    // Necessary, add an optional polling event.
    if (this.props.adjustOnResize) {
      window.addEventListener('resize', this.debouncedAdjustLocation);
    }
    if (this.props.adjustOnScroll) {
      window.addEventListener('scroll', this.debouncedAdjustLocation);
    }

    this.adjustLocation(); // Initial adjust, without debounce
  }

  componentDidUpdate() {
    if (this.props.absolute) {
      this.handleAbsolute();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.debouncedAdjustLocation);
    window.removeEventListener('scroll', this.debouncedAdjustLocation);
    document.body.removeEventListener('click', this.throttledHandleDismiss);
  }

  /* Handle edge adjustments for absolutely positioned tooltip if extending past viewport */
  absoluteEdgeAdjust() {
    // Get positions of inner tooltip box, which will be adjusted
    // if the box extends off the screen

    const innerBoxPosition = getComputedStyle(this._innerBox);
    const innerBoxRightValue = removePx(innerBoxPosition.right);
    const innerBoxLeftValue = removePx(innerBoxPosition.left);

    // If the offset values are negative, that is the amount off the screen
    // The box is, and thus is the adjustment amount
    const offScreenRight = innerBoxRightValue < 0;
    const offScreenLeft = innerBoxLeftValue < 0;
    const offScreen = offScreenRight || offScreenLeft;

    if (!offScreen) {
      // just center the arrow within the tooltip
      const innerBoxRect = this._innerBox.getBoundingClientRect();
      const newArrowLeft = `${innerBoxRect.width / 2}px`;
      this._arrow.style.left = newArrowLeft;
      return;
    }

    let adjustment;
    let newInnerBoxLeft;

    if (offScreenRight) {
      adjustment = innerBoxRightValue;
      newInnerBoxLeft = `${
        removePx(getComputedStyle(this._innerBox).left) + adjustment
      }px`;
    } else if (offScreenLeft) {
      adjustment = innerBoxLeftValue;
      newInnerBoxLeft = `${
        removePx(getComputedStyle(this._innerBox).left) - adjustment
      }px`;
    }

    // Shift position of inner box
    this._innerBox.style.left = newInnerBoxLeft;

    // Arrow positioning works a little differently, easiest solution
    // Is to simply position it based on where the tooltip element is
    const currentArrowLeft = removePx(getComputedStyle(this._arrow).left);
    const arrowRect = this._arrow.getBoundingClientRect();
    const arrowAdjustment =
      this._element.getBoundingClientRect().left -
      arrowRect.left -
      arrowRect.width / 2;
    const newArrowLeft = `${currentArrowLeft + arrowAdjustment}px`;
    this._arrow.style.left = newArrowLeft;
  }

  // If props.absolute is specified, the tooltip inner box is placed before the closing body
  // tag, positioned absolutely, and given offset based on it's initial position relative to
  // the tooltip (outer box).  This allows the tooltip to remain visible even if it would otherwise
  // be cut off by a containing element with a non-visible overflow value.
  absoluteOverlay() {
    // Remove the innerBox element from the DOM, and reinsert it outside of the containing element before closing </body> tag
    // The node is removed and readded every time the tooltip arises, to prevent memory leak
    this._innerBox.parentElement.removeChild(this._innerBox);
    document.body.appendChild(this._innerBox);

    // Position the innerBox element absolutely, then assign offset based
    // On it's position relative to the containing Tooltip element
    this._innerBox.style.position = 'absolute';

    // On mouseenter, the coordinates of the tooltip element is determined, with relation to the viewport
    const tooltipCoordinates = this._element.getBoundingClientRect();

    // Assign left and top properties to innerBox, positioning relatively to the tooltip element
    this._innerBox.style.top = `${
      tooltipCoordinates.top + this.relativePosition.top
    }px`;
    this._innerBox.style.left = `${
      tooltipCoordinates.left + this.relativePosition.left
    }px`;
    this._innerBox.style.height = this.innerBoxHeight;
  }

  adjustLocation() {
    const rect = this._innerBox.getBoundingClientRect();

    // Empirically determine the effective viewport size with a test element.
    // This is because window.innerWidth seems to give slightly off answers.
    const viewport = testViewportSize();
    const margins = {
      width: rect.right - rect.left, // Used for arrow margin
      leftMargin: rect.left,
      rightMargin: viewport.width - rect.right,
      topMargin: rect.top,
      bottomMargin: viewport.height - rect.bottom,
    };

    // Pre-emptive check here to prevent excessive refresh: Only sets state if
    // Needs special margin treatment, or transitioning from such a state
    if (Tooltip.atEdge(margins, 'left', 'right', 'top', 'bottom')) {
      // Transitioning from regular case into page edge case
      this.setState(margins);
    } else if (Tooltip.atEdge(this.state, 'left', 'right', 'top', 'bottom')) {
      // Transitioning from page edge case into regular case
      this.setState(NO_MARGINS);
    }
  }

  /* This calculates the position of the tooltip's inner box element relative to the tooltip element. */
  calculateRelativePosition() {
    const tooltipCoordinates = this._element.getBoundingClientRect();
    const tooltipInnerCoordinates = this._innerBox.getBoundingClientRect();

    const topDifferential =
      tooltipInnerCoordinates.top - tooltipCoordinates.top;
    const leftDifferential =
      tooltipInnerCoordinates.left - tooltipCoordinates.left;

    const tooltipHeight = getComputedStyle(this._innerBox).height;

    this.relativePosition = {
      top: topDifferential,
      left: leftDifferential,
    };
    this.innerBoxHeight = tooltipHeight;
    this.relativePositionCalculated = true;
  }

  render() {
    const innerClassName = [
      'Tooltip-inner',
      `Tooltip-inner--${this.props.tooltipType}`,
    ];

    const props = {className: 'Tooltip'};
    const innerProps = {};
    const arrowProps = {className: 'Tooltip-arrow'};

    props.style = {};
    innerProps.style = {};

    if (!this.props.visible) {
      // If being rendered, but invisible, ensure display is none
      innerProps.style.display = 'none';
    }

    if (this.props.position) {
      // Should reset to particular location
      props.style.left = this.props.position.left;
      props.style.top = this.props.position.top;
      props.style.position = 'absolute';
    }

    innerProps.ref = (component) => (this._innerBox = component);

    // Apply offset adjustments
    if (Tooltip.atEdge(this.state, 'left')) {
      // Computes offset of black box
      innerProps.style = {marginLeft: PAGE_MIN_MARGIN - this.state.leftMargin};

      // Compute the maximum arrow offset, which maintains minimum margin
      const maxArrowOffset = (this.state.width - ARROW_MIN_MARGIN) / 2;
      const calculatedArrowOffset = PAGE_MIN_MARGIN - this.state.leftMargin;
      const arrowOffset = Math.min(calculatedArrowOffset, maxArrowOffset);
      arrowProps.style = {marginLeft: -arrowOffset}; // Negative <--
    }

    if (Tooltip.atEdge(this.state, 'right')) {
      // Computes offset of black box
      innerProps.style = {
        marginLeft: -(PAGE_MIN_MARGIN - this.state.rightMargin),
      };

      // Compute the maximum arrow offset, which maintains minimum margin
      const maxArrowOffset = this.state.width / 2 - ARROW_MIN_MARGIN;
      const calculatedArrowOffset = -PAGE_MIN_MARGIN - this.state.rightMargin;
      const arrowOffset = Math.min(calculatedArrowOffset, maxArrowOffset);
      arrowProps.style = {marginLeft: arrowOffset}; // Positive -->
    }

    arrowProps.ref = (component) => (this._arrow = component);

    // Logic to determine if it goes on top or bottom (could probably be
    // Algebraically simplified, but this seemed straightforward)
    let above = this.props.above;
    if (this.props.adjustVertical) {
      if (Tooltip.atEdge(this.state, 'bottom')) {
        above = true;
      } else if (Tooltip.atEdge(this.state, 'top')) {
        above = false;
      }
    }

    // Now push extra class
    innerClassName.push(`Tooltip-inner--${above ? 'above' : 'below'}`);
    innerProps.className = innerClassName.join(' ');

    return (
      <span {...props} ref={(element) => (this._element = element)}>
        <span {...innerProps} ref={(element) => (this._innerBox = element)}>
          <div {...arrowProps} />
          <div className={'Tooltip-contents'}>{this.props.children}</div>
          {!this.props.onCancel ? null : (
            <div className={'Tooltip-button-left'}>
              <Button narrow={true} onClick={this.props.onCancel} type={'red'}>
                {this.props.cancelLabel}
              </Button>
            </div>
          )}
          {!this.props.onConfirm ? null : (
            <div className={'Tooltip-button-right'}>
              <Button
                narrow={true}
                onClick={this.props.onConfirm}
                type={'green'}>
                {this.props.confirmLabel}
              </Button>
            </div>
          )}
        </span>
      </span>
    );
  }

  handleAbsolute() {
    if (this.props.visible) {
      // The first time the tooltip is rendered visibly, relative position is calculated
      // When the tooltip has display: none, this is impossible
      if (!this.relativePositionCalculated) {
        this.calculateRelativePosition();
      }
      this.absoluteOverlay();
      this.absoluteEdgeAdjust();
    }
  }

  handleDismiss(event) {
    // Only cancel if the click was not within this element
    if (!this._element.contains(event.target)) {
      this.props.onDismiss();
    }
  }
}
